import { ROUTES } from '@/app/router/routes';
import { JOB_SHARE_REF } from '@/features/jobs/utils/jobOrigin';

/**
 * Post-login / post-MFA destination.
 * Mirrors the Flutter WebLoginPageConsumer + WebMfaVerificationPage listeners,
 * which route on the ProfileInput passed to /login.
 */

/** ProfileInput fields callers pass as /login route state. */
export interface LoginReturnInput {
  saveJobInProgress?: boolean;
  onSimiliarJobs?: boolean;
  previousPage?: string;
  jobDisplayId?: string;
  clickedOn?: string;
}

/** /login (and /mfa) route state: ProfileInput fields, or `from` set by ProtectedRoute/AccountGuard. */
export interface LoginRedirectState extends LoginReturnInput {
  from?: { pathname: string; search?: string; hash?: string };
}

export interface PostLoginRedirect {
  path: string;
  state?: { profileInput: LoginReturnInput };
  /**
   * screen_view for this navigation — the route name (and analyticsClass) Flutter passes to
   * NavigationService.navigateTo(), which logs it. `from` has no Flutter counterpart, so it
   * logs its own path.
   */
  screenName: string;
  screenClass?: string;
}

export function resolvePostLoginRedirect(routeState: unknown): PostLoginRedirect {
  const s = (routeState ?? {}) as LoginRedirectState;
  const profileInput: LoginReturnInput = {
    saveJobInProgress: s.saveJobInProgress,
    onSimiliarJobs: s.onSimiliarJobs,
    previousPage: s.previousPage,
    jobDisplayId: s.jobDisplayId,
    clickedOn: s.clickedOn,
  };
  const state = { profileInput };
  const jobDisplayId = s.jobDisplayId ?? '';

  if (s.clickedOn === 'APPLIED' || s.previousPage === ROUTES.PROFILE) {
    return { path: ROUTES.PROFILE, state, screenName: ROUTES.PROFILE };
  }
  if (s.previousPage === ROUTES.JOBS && jobDisplayId) {
    return {
      path: `${ROUTES.JOBS}/${jobDisplayId}?ref=${JOB_SHARE_REF.INTERNAL}`,
      state,
      screenName: ROUTES.JOBS,
    };
  }
  if (s.previousPage === ROUTES.JOBS) {
    return { path: ROUTES.JOBS, state, screenName: ROUTES.JOBS };
  }
  if (s.previousPage === ROUTES.SAVED_JOBS) {
    // Header "Saved Jobs" → login: Flutter opens the profile on My Jobs › Saved
    return { path: ROUTES.VIEW_JOBS_SAVED, state, screenName: ROUTES.PROFILE };
  }
  if (s.previousPage === ROUTES.EXTRACTED_RESUME) {
    return { path: ROUTES.EXTRACTED_RESUME, state, screenName: ROUTES.EXTRACTED_RESUME };
  }
  if (s.previousPage === ROUTES.APPLICATION && jobDisplayId) {
    return {
      path: `${ROUTES.APPLICATION}/${jobDisplayId}`,
      screenName: ROUTES.APPLICATION,
      screenClass: 'home_page',
    };
  }
  if (s.from?.pathname) {
    return {
      path: `${s.from.pathname}${s.from.search ?? ''}${s.from.hash ?? ''}`,
      screenName: s.from.pathname,
    };
  }
  return { path: ROUTES.HOME, state, screenName: ROUTES.HOME };
}
